import { z } from "zod";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const AI_COSTS_TABS = [
  { id: "usage-type", label: "By usage type" },
  { id: "accounts", label: "Top accounts" },
  { id: "models", label: "By model" },
  { id: "features", label: "By feature" },
  { id: "image-models", label: "Image models" },
] as const;

export type AiCostsTab = (typeof AI_COSTS_TABS)[number]["id"];

export type AiCostsBreakdownTab = Exclude<AiCostsTab, "image-models">;

export const AI_COSTS_DEFAULT_SORT = "costMicroUsd";

const SORT_COLUMNS = [
  "name",
  "costMicroUsd",
  "events",
  "failedEvents",
  "inputTokens",
  "cachedInputTokens",
  "outputTokens",
  "images",
  "megapixels",
] as const;

export const aiCostsSearchZod = z.object({
  dir: z.enum(["asc", "desc"]).catch("desc"),
  from: z.string().regex(ISO_DATE).catch("").optional(),
  page: z.number().int().min(1).catch(1),
  period: z.enum(["this-month", "last-30-days", "custom"]).catch("this-month"),
  sort: z.enum(SORT_COLUMNS).catch(AI_COSTS_DEFAULT_SORT),
  tab: z
    .enum(["usage-type", "accounts", "models", "features", "image-models"])
    .catch("usage-type"),
  to: z.string().regex(ISO_DATE).catch("").optional(),
});

export type AiCostsSearch = z.infer<typeof aiCostsSearchZod>;

export type AiCostsPeriod = AiCostsSearch["period"];

export type AiCostsRange = { from: Date; to: Date };

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function parseLocalDate(isoDate: string): Date {
  const [year, month, day] = isoDate.split("-").map(Number) as [
    number,
    number,
    number,
  ];

  return new Date(year, month - 1, day);
}

export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${date.getFullYear()}-${month}-${day}`;
}

// Ranges end exclusive at local midnight, so "to" is the day after the last day shown.
export function resolveAiCostsRange(
  search: Pick<AiCostsSearch, "from" | "period" | "to">,
  now = new Date(),
): AiCostsRange | null {
  if (search.period === "this-month") {
    return {
      from: new Date(now.getFullYear(), now.getMonth(), 1),
      to: new Date(now.getFullYear(), now.getMonth() + 1, 1),
    };
  }

  if (search.period === "last-30-days") {
    const to = addDays(startOfDay(now), 1);

    return { from: addDays(to, -30), to };
  }

  if (!search.from || !search.to || search.from > search.to) return null;

  return {
    from: parseLocalDate(search.from),
    to: addDays(parseLocalDate(search.to), 1),
  };
}

export function toCustomDates(
  range: AiCostsRange,
): Pick<AiCostsSearch, "from" | "to"> {
  return {
    from: toIsoDate(range.from),
    to: toIsoDate(addDays(range.to, -1)),
  };
}
